import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ADVANCE_SECONDS,
  COUNTDOWN_SECONDS,
  FIRST_CALL_MINUTES,
  MAX_ATTEMPTS,
  MAX_CALLS_PER_ATTEMPT,
  RESTART_AFTER_WORKING_DAYS,
  type CallSequenceSettings,
  type CallSequenceSettingsResponse,
  type OrganizationSettingsView,
} from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { callSequenceSettingsQuery, settingsQuery, useSaveCallSequenceSettings, useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { DataTable } from "../../design/components/DataTable";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { Select } from "../../design/components/Select";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import {
  ADDED_ATTEMPT_CALLS,
  describeSequenceChanges,
  draftExample,
  draftOf,
  editsWith,
  exampleLines,
  problemText,
  saveBodyOf,
  sequenceProblem,
  totalText,
  type CallSequenceEdits,
} from "./call-sequence-draft";
import { clockOptions } from "./calendar-draft";
import { ConfirmChangesDialog, draftSummary, LeaveDialog, useLeaveGuard, type Feedback } from "./draft-dialogs";
import "./calendar.css";

// Impostazioni → «Sequenza di chiamate» (piano M5, Task 13; D11; mock-up `regole.html?sezione=sequenza`, senza la colonna del
// WhatsApp, che arriva con M6, né i minuti fra una chiamata e l'altra, questione 1): i tentativi con le chiamate di fila, le mezze
// giornate, la prima chiamata, l'attesa della cascata e, dopo «Chiudi la chiamata», i secondi prima del Lead successivo (decisione
// del 9 ottobre 2026), la casella «Senza risposta, richiama da solo…» (decisione dell'11 ottobre 2026) e la tendina «Senza contatto,
// la sequenza riparte da capo dopo» (SEQ-14, letta dal giro del server a ogni giro), con l'esempio di un Lead che entra adesso. Sul
// modello di «Calendario, fuso e lingua»: una bozza (i soli campi toccati), la barra delle modifiche, «Salva…» con «Cosa cambia per
// il team» e una sola richiesta con la regola intera e la versione letta; una versione cambiata nel frattempo (`409
// version_conflict`, o la rilettura che la porta) rimette le modifiche sopra la regola nuova e lo dice. La vede ogni persona attiva;
// la cambia chi ha `settings.call_sequence` (`canEdit` del server), mai il Super admin di Gioia365 dall'interfaccia (questione 43).
// Le sequenze già partite tengono la loro regola: la nuova vale per quelle che partono da adesso
// (i secondi del Power Dialer, prima e dopo la chiamata, e la casella del richiamo da solo si rileggono a ogni vista).

// «Lo passo io» (`advanceSeconds: null`) non è un numero: nella tendina è il valore a parte.
const ADVANCE_MANUAL = "manual";
// «Mai» (`restartAfterWorkingDays: null`) non è un numero: nella tendina è il valore a parte, come «Lo passo io».
const RESTART_NEVER = "never";

// Gli orari dei due confini, a passi di mezz'ora, come nel mock-up.
const MORNING_END_RANGE = ["11:00", "14:00"] as const;
const AFTERNOON_START_RANGE = ["12:00", "15:30"] as const;

export function CallSequencePage() {
  return (
    <SettingsLayout section="callSequence">
      <CallSequenceSection />
    </SettingsLayout>
  );
}

function CallSequenceSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const rule = useQuery(callSequenceSettingsQuery(tab));
  // Il calendario dell'azienda: i confini delle mezze giornate si controllano su di lui, e l'esempio della bozza lo segue.
  const settings = useQuery(settingsQuery(tab));
  const organization = access.context.organization.name;
  const allowed = access.can("settings.call_sequence") && !access.isPlatform;
  const editable = allowed && rule.data?.canEdit === true;
  const failed = rule.isError ? rule : settings.isError ? settings : null;

  let body: ReactNode;
  if (rule.data && settings.data) {
    body = <CallSequenceEditor base={rule.data} calendar={settings.data} editable={editable} />;
  } else if (failed) {
    body = (
      <div className="g-calendar__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, failed.error)}
        </Reason>
        <Button onClick={() => void failed.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-calendar__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      <AboutBox title={t("callSequenceSettings.about")}>
        <p>{t("callSequenceSettings.aboutText")}</p>
      </AboutBox>
      {!allowed || (rule.data && !rule.data.canEdit) ? (
        <Reason>{access.isPlatform ? t("callSequenceSettings.readOnlyPlatform", { organization }) : t("callSequenceSettings.readOnly", { organization })}</Reason>
      ) : null}
      {body}
    </>
  );
}

function fieldsOf(details: Record<string, unknown> | undefined): string[] {
  const value = details?.fields;
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

/** Un rifiuto del server, spiegato: i confini che il server controlla sul calendario parlano come la barra. */
function saveError(t: Translate, error: unknown, problem: string | null): string {
  if (error instanceof ApiRequestError && error.code === "validation_failed" && problem !== null) {
    const fields = fieldsOf(error.details);
    if (fields.includes("settings.morningEnd") || fields.includes("settings.afternoonStart")) return problem;
  }
  return messageForError(t, error);
}

type EditorProps = { base: CallSequenceSettingsResponse; calendar: OrganizationSettingsView; editable: boolean };

function CallSequenceEditor({ base, calendar, editable }: EditorProps) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const saveRule = useSaveCallSequenceSettings(tab);
  const baseId = useId();
  const [edits, setEdits] = useState<CallSequenceEdits>({});
  // La finestra «Cosa cambia per il team», con il rifiuto del server se il salvataggio si è fermato.
  const [confirming, setConfirming] = useState<{ error: Feedback | null } | null>(null);
  const [saving, setSaving] = useState(false);
  // Un conflitto di versione: la pagina lo dice sopra la bozza.
  const [notice, setNotice] = useState<Feedback | null>(null);
  // La versione su cui la bozza è stata pensata: se la rilettura ne porta un'altra mentre ci sono modifiche (anche un calendario
  // cambiato), le modifiche restano sopra la regola nuova e la pagina lo dice, come per un 409 (il modello di «Campi della scheda»).
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  // Un salvataggio in corso, letto anche da un secondo clic che arriva prima del nuovo disegno.
  const savingNow = useRef(false);
  // Dopo «+ Aggiungi un tentativo» e «Togli l'ultimo tentativo» il focus torna su «+ Aggiungi un tentativo», come nel mock-up
  // (il cestino sparisce con la riga); con dieci tentativi, che non lo hanno più, sul cestino dell'ultimo.
  const addRef = useRef<HTMLButtonElement>(null);
  const removeRef = useRef<HTMLButtonElement>(null);
  const [refocus, setRefocus] = useState(false);

  const saved = base.settings;
  // Senza il permesso (che può sparire con un contesto riletto) si vede il salvato, non una bozza che non si può salvare.
  const draft = useMemo(() => draftOf(saved, editable ? edits : {}), [saved, editable, edits]);
  const changes = describeSequenceChanges(saved, draft, t);
  const dirty = editable && changes.length > 0;
  const blocker = useLeaveGuard(dirty);
  const problemCode = sequenceProblem(draft, calendar);
  const problem = problemCode ? problemText(t, problemCode) : null;
  const hasEdits = editable && Object.keys(edits).length > 0;
  // L'esempio del server per la regola salvata; con una bozza, lo stesso calcolo sulla bozza e sul calendario dell'azienda.
  const example = useMemo(() => (dirty ? draftExample(draft, calendar, new Date()) : base.example), [dirty, draft, calendar, base.example]);
  const total = totalText(t, draft, example);
  const lines = exampleLines(t, example, calendar.timeZone);

  useEffect(() => {
    if (!hasEdits) {
      if (draftVersion !== null) setDraftVersion(null);
      return;
    }
    if (draftVersion === null) {
      setDraftVersion(base.version);
      return;
    }
    if (base.version === draftVersion) return;
    setDraftVersion(base.version);
    // Il proprio salvataggio rilegge la regola mentre salva: quello non è un conflitto.
    if (savingNow.current) return;
    setConfirming(null);
    setNotice({ text: (tr) => tr("callSequenceSettings.conflict") });
  }, [hasEdits, base.version, draftVersion]);

  useEffect(() => {
    if (!refocus) return;
    (addRef.current ?? removeRef.current)?.focus();
    setRefocus(false);
  }, [refocus]);

  const disabled = !editable || saving;

  function change(update: Partial<CallSequenceSettings>) {
    if (!editable || savingNow.current) return;
    setEdits((current) => editsWith(saved, current, update));
  }

  function setCalls(index: number, calls: number) {
    change({ attempts: draft.attempts.map((count, at) => (at === index ? calls : count)) });
  }

  function discard() {
    setEdits({});
    setNotice(null);
    toast.show(t("settings.draft.discarded"));
  }

  async function save() {
    if (!editable || savingNow.current || problemCode !== null || changes.length === 0) return;
    savingNow.current = true;
    setSaving(true);
    setConfirming({ error: null });
    try {
      await saveRule.mutateAsync(saveBodyOf(draft, base.version));
      // La conferma si chiude prima dell'avviso (D16).
      setConfirming(null);
      setEdits({});
      setNotice(null);
      toast.show(t("callSequenceSettings.saved"), "success");
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // La regola si rilegge: le modifiche restano sopra quella nuova, da controllare.
        setConfirming(null);
        setNotice({ text: (tr) => tr("callSequenceSettings.conflict") });
      } else {
        // La finestra resta con la bozza e dice perché: si torna alle modifiche o si riprova.
        setConfirming({ error: { text: (tr) => saveError(tr, error, problemCode ? problemText(tr, problemCode) : null) } });
      }
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  }

  function stay() {
    if (blocker.status === "blocked") blocker.reset();
  }

  function leave() {
    setEdits({});
    if (blocker.status === "blocked") blocker.proceed();
  }

  const callOptions = Array.from({ length: MAX_CALLS_PER_ATTEMPT }, (_, index) => {
    const count = index + 1;
    return { value: String(count), label: count === 1 ? t("callSequenceSettings.callsOne") : t("callSequenceSettings.callsMany", { count }) };
  });
  const last = draft.attempts.length - 1;

  return (
    <>
      {notice ? (
        <Reason tone="warning" role="alert">
          {notice.text(t)}
        </Reason>
      ) : null}

      <section className="g-calendar__section" aria-labelledby={`${baseId}-attempts`}>
        <h2 id={`${baseId}-attempts`}>{t("callSequenceSettings.attempts")}</h2>
        <DataTable caption={t("callSequenceSettings.attempts")} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("callSequenceSettings.attempt")}</th>
              <th scope="col">{t("callSequenceSettings.calls")}</th>
              <th scope="col">{t("callSequenceSettings.when")}</th>
              <th scope="col">
                <span className="g-visually-hidden">{t("callSequenceSettings.remove")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {draft.attempts.map((calls, index) => (
              // L'indice è il numero del tentativo: i tentativi si aggiungono e si tolgono soltanto in fondo.
              <tr key={index}>
                <th scope="row">{t("callSequenceSettings.attemptNumber", { n: index + 1 })}</th>
                <td>
                  <Select
                    id={`${baseId}-calls-${index}`}
                    label={t("callSequenceSettings.callsOf", { n: index + 1 })}
                    hideLabel
                    className="g-calendar__time"
                    value={String(calls)}
                    onChange={(value) => setCalls(index, Number(value))}
                    options={callOptions}
                    disabled={disabled}
                  />
                </td>
                <td>
                  {index === 0 ? t("callSequenceSettings.whenFirst", { minutes: draft.firstCallMinutes }) : t("callSequenceSettings.whenNext")}
                </td>
                <td>
                  {editable && index === last && last > 0 ? (
                    <Button
                      ref={removeRef}
                      variant="ghost"
                      className="g-calendar__remove"
                      aria-label={t("callSequenceSettings.removeLast")}
                      disabled={saving}
                      onClick={() => {
                        change({ attempts: draft.attempts.slice(0, -1) });
                        setRefocus(true);
                      }}
                    >
                      <Icon name="trash" />
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
        {editable && draft.attempts.length < MAX_ATTEMPTS ? (
          <p>
            <Button
              ref={addRef}
              onClick={() => {
                change({ attempts: [...draft.attempts, ADDED_ATTEMPT_CALLS] });
                setRefocus(true);
              }}
              disabled={saving}
            >
              {t("callSequenceSettings.addAttempt")}
            </Button>
          </p>
        ) : null}
        {total !== null ? (
          <Reason tone="info">
            <strong>{total}</strong>
          </Reason>
        ) : null}
        {lines.length > 0 ? (
          <div className="g-calendar__section" aria-labelledby={`${baseId}-example`}>
            <h3 id={`${baseId}-example`}>{t("callSequenceSettings.example")}</h3>
            <ul>
              {lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-halves`}>
        <h2 id={`${baseId}-halves`}>{t("callSequenceSettings.halves")}</h2>
        <div className="g-calendar__add">
          <Select
            id={`${baseId}-morning-end`}
            label={t("callSequenceSettings.morningEnd")}
            className="g-calendar__time"
            value={draft.morningEnd}
            onChange={(morningEnd) => change({ morningEnd })}
            options={clockOptions(MORNING_END_RANGE[0], MORNING_END_RANGE[1], draft.morningEnd).map((clock) => ({ value: clock, label: clock }))}
            disabled={disabled}
          />
          <Select
            id={`${baseId}-afternoon-start`}
            label={t("callSequenceSettings.afternoonStart")}
            className="g-calendar__time"
            value={draft.afternoonStart}
            onChange={(afternoonStart) => change({ afternoonStart })}
            options={clockOptions(AFTERNOON_START_RANGE[0], AFTERNOON_START_RANGE[1], draft.afternoonStart).map((clock) => ({ value: clock, label: clock }))}
            disabled={disabled}
          />
        </div>
        <Checkbox
          id={`${baseId}-friday`}
          label={t("callSequenceSettings.fridayOne")}
          checked={draft.fridayOne}
          disabled={disabled}
          onChange={(fridayOne) => change({ fridayOne })}
        />
        <p className="g-calendar__muted">{t("callSequenceSettings.halvesNote")}</p>
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-first`}>
        <h2 id={`${baseId}-first`}>{t("callSequenceSettings.firstCall")}</h2>
        <Select
          id={`${baseId}-first-select`}
          label={t("callSequenceSettings.firstCall")}
          hideLabel
          className="g-calendar__choice"
          value={String(draft.firstCallMinutes)}
          onChange={(value) => {
            const minutes = FIRST_CALL_MINUTES.find((choice) => String(choice) === value);
            if (minutes !== undefined) change({ firstCallMinutes: minutes });
          }}
          options={FIRST_CALL_MINUTES.map((minutes) => ({ value: String(minutes), label: t("callSequenceSettings.firstCallMinutes", { minutes }) }))}
          disabled={disabled}
        />
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-restart`}>
        <h2 id={`${baseId}-restart`}>{t("callSequenceSettings.restart")}</h2>
        <Select
          id={`${baseId}-restart-select`}
          label={t("callSequenceSettings.restart")}
          hideLabel
          hint={t("callSequenceSettings.restartHint")}
          className="g-calendar__choice"
          value={draft.restartAfterWorkingDays === null ? RESTART_NEVER : String(draft.restartAfterWorkingDays)}
          onChange={(value) => {
            if (value === RESTART_NEVER) {
              change({ restartAfterWorkingDays: null });
              return;
            }
            const days = RESTART_AFTER_WORKING_DAYS.find((choice) => String(choice) === value);
            if (days !== undefined) change({ restartAfterWorkingDays: days });
          }}
          options={[
            ...RESTART_AFTER_WORKING_DAYS.map((days) => ({ value: String(days), label: t("callSequenceSettings.restartDays", { days }) })),
            { value: RESTART_NEVER, label: t("callSequenceSettings.restartNever") },
          ]}
          disabled={disabled}
        />
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-cascade`}>
        <h2 id={`${baseId}-cascade`}>{t("callSequenceSettings.cascade")}</h2>
        <Select
          id={`${baseId}-countdown`}
          label={t("callSequenceSettings.countdown")}
          hint={t("callSequenceSettings.countdownHint")}
          className="g-calendar__choice"
          value={String(draft.countdownSeconds)}
          onChange={(value) => {
            const seconds = COUNTDOWN_SECONDS.find((choice) => String(choice) === value);
            if (seconds !== undefined) change({ countdownSeconds: seconds });
          }}
          options={COUNTDOWN_SECONDS.map((seconds) => ({
            value: String(seconds),
            label: seconds === 0 ? t("callSequenceSettings.countdownNone") : t("callSequenceSettings.countdownSeconds", { seconds }),
          }))}
          disabled={disabled}
        />
      </section>

      <section className="g-calendar__section" aria-labelledby={`${baseId}-cascade-after`}>
        <h2 id={`${baseId}-cascade-after`}>{t("callSequenceSettings.cascadeAfter")}</h2>
        <Select
          id={`${baseId}-advance`}
          label={t("callSequenceSettings.advance")}
          hint={t("callSequenceSettings.advanceHint")}
          className="g-calendar__choice"
          value={draft.advanceSeconds === null ? ADVANCE_MANUAL : String(draft.advanceSeconds)}
          onChange={(value) => {
            if (value === ADVANCE_MANUAL) {
              change({ advanceSeconds: null });
              return;
            }
            const seconds = ADVANCE_SECONDS.find((choice) => String(choice) === value);
            if (seconds !== undefined) change({ advanceSeconds: seconds });
          }}
          options={[
            ...ADVANCE_SECONDS.map((seconds) => ({
              value: String(seconds),
              label: seconds === 0 ? t("callSequenceSettings.advanceNow") : t("callSequenceSettings.advanceSeconds", { seconds }),
            })),
            { value: ADVANCE_MANUAL, label: t("callSequenceSettings.advanceManual") },
          ]}
          disabled={disabled}
        />
        <Checkbox
          id={`${baseId}-auto-redial`}
          label={t("callSequenceSettings.autoRedial")}
          hint={t("callSequenceSettings.autoRedialHint")}
          checked={draft.autoRedial}
          disabled={disabled}
          onChange={(autoRedial) => change({ autoRedial })}
        />
      </section>

      {dirty ? (
        <SaveBar
          summary={draftSummary(t, changes.length)}
          problem={problem ?? undefined}
          onDiscard={discard}
          onSave={() => setConfirming({ error: null })}
          saveLabel={t("settings.draft.save")}
          saving={saving}
        />
      ) : null}

      {confirming && dirty ? (
        <ConfirmChangesDialog changes={changes} error={confirming.error} saving={saving} onBack={() => setConfirming(null)} onApply={() => void save()} />
      ) : null}

      {blocker.status === "blocked" ? <LeaveDialog onStay={stay} onLeave={leave} /> : null}
    </>
  );
}
